import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Hourglass } from 'lucide-react';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { getDateFromToFilterFields } from '@/utils/filterFields';
import { pendingSummaryQueryOptions } from '../api/pendingSummary.api';
import { getPendingSummaryColumns } from '../components/PendingSummaryColumns';
import { usePendingSummaryFilters } from '../hooks/usePendingSummaryFilters';
import { PENDING_COLUMNS } from '../types/pendingSummary.types';

export function PendingSummaryPage() {
  const controller = usePendingSummaryFilters();
  const { search } = controller;
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pendingSummaryQueryOptions(search),
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const columns = useMemo(() => getPendingSummaryColumns(), []);
  const totals = data?.meta.totals;

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="DCR & DER Pending"
        titleAddon={<DateRangeBadge dateFrom={search.dateFrom} dateTo={search.dateTo} />}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel
          fields={getDateFromToFilterFields().map((field) => ({
            ...field,
            isClearable: true,
          }))}
          {...getFilterPanelProps(controller)}
        />
      </FilterPanelCollapse>

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the pending summary"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            isFetching={isFetching}
            stats={[
              {
                key: 'entryPersonQty',
                label: 'Entry Persons',
                value: totals?.entryPersonQty,
              },
              ...PENDING_COLUMNS.map((column) => ({
                ...column,
                value: totals?.[column.key],
              })),
              {
                key: 'total',
                label: 'Total',
                value: totals?.total,
              },
            ]}
          />

          <DataTable
            data={data?.items ?? []}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="reportingName"
            emptyIcon={Hourglass}
            emptyMessage="Nothing is pending"
            emptyHint="No DCR or DER is waiting for these dates."
          />
        </>
      )}
    </div>
  );
}
